import {
  getAskUserAnswerText,
  getAskUserToolParts,
  isAskUserPartAnswered,
  isAskUserToolPart,
  parseAskUserToolInput,
  parseAskUserToolOutput,
  type AskUserToolPart,
} from './chat-ask-user-flow';
import type { ChatUIMessage } from './types';

type ChatPart = ChatUIMessage['parts'][number];

/** Réponse à une question `ask_user`, rendue comme une bulle utilisateur. */
export type AskUserAnswerView = {
  source: 'answered_in_text' | 'widget';
  text: string;
  toolCallId: string;
};

export type ConversationItem =
  | {
    /** Dernier segment assistant portant du texte : seul porteur des actions du message. */
    carriesActions: boolean;
    key: string;
    kind: 'message';
    message: ChatUIMessage;
  }
  | {
    answer: AskUserAnswerView;
    key: string;
    kind: 'ask-user-answer';
  };

function isStepStart(part: ChatPart) {
  return part.type === 'step-start';
}

/**
 * L'historique rechargé ne contient pas de `step-start` et le backend y range
 * la question en attente EN PREMIER (`ensurePendingAskUserStoredParts`) alors que
 * le flux live la place après le texte qui l'introduit. On rétablit l'ordre
 * chronologique : la question clôt son message persisté.
 */
function normalizeRawParts(message: ChatUIMessage): ChatPart[] {
  if (message.parts.some(isStepStart) || !message.parts.some(isAskUserToolPart)) {
    return message.parts;
  }

  return [
    ...message.parts.filter((part) => !isAskUserToolPart(part)),
    ...message.parts.filter(isAskUserToolPart),
  ];
}

function hasRenderableContent(parts: ChatPart[]) {
  return parts.some((part) => !isStepStart(part));
}

function hasText(parts: ChatPart[]) {
  return parts.some((part) => part.type === 'text' && part.text.trim().length > 0);
}

function buildAnswerView(part: AskUserToolPart): AskUserAnswerView | null {
  const output = parseAskUserToolOutput(part.output);
  const text = getAskUserAnswerText(parseAskUserToolInput(part.input), output);

  if (!output || !text) {
    return null;
  }

  return { source: output.source, text, toolCallId: part.toolCallId };
}

/**
 * Projette une suite de messages assistant consécutifs (un tour avec questions :
 * le message de la question puis ses continuations) en segments chronologiques :
 * contenu avant la question -> bulle de réponse -> suite de la réponse, et ainsi
 * de suite. Les parts d'un segment restent intactes (cartes docgen, outils,
 * sources) : seul le point de coupe change.
 */
function projectAssistantRun(run: ChatUIMessage[]): ConversationItem[] {
  const base = run[0]!;
  const parts = run.flatMap(normalizeRawParts);
  const bestByToolCallId = new Map(
    getAskUserToolParts({ parts }).map((part) => [part.toolCallId, part]),
  );
  const items: ConversationItem[] = [];
  const answeredToolCallIds = new Set<string>();
  let current: ChatPart[] = [];
  let answerCount = 0;

  const flush = () => {
    if (hasRenderableContent(current)) {
      items.push({
        carriesActions: false,
        key: answerCount === 0 ? base.id : `${base.id}::segment-${answerCount}`,
        kind: 'message',
        message: { ...base, id: answerCount === 0 ? base.id : `${base.id}::segment-${answerCount}`, parts: current },
      });
    }
    current = [];
  };

  for (const part of parts) {
    if (!isAskUserToolPart(part)) {
      current.push(part);
      continue;
    }

    const best = bestByToolCallId.get(part.toolCallId) ?? part;

    if (!isAskUserPartAnswered(best)) {
      current.push(part);
      continue;
    }

    // Doublons d'un même toolCallId : la réponse n'est projetée qu'une fois.
    if (answeredToolCallIds.has(part.toolCallId)) {
      continue;
    }

    answeredToolCallIds.add(part.toolCallId);
    const answer = buildAnswerView(best);

    if (!answer) {
      continue;
    }

    flush();
    answerCount += 1;
    items.push({
      answer,
      key: `${base.id}::answer::${answer.toolCallId}`,
      kind: 'ask-user-answer',
    });
  }

  flush();

  let lastTextIndex = -1;
  items.forEach((item, index) => {
    if (item.kind === 'message' && hasText(item.message.parts)) {
      lastTextIndex = index;
    }
  });
  return items.map((item, index) => (
    item.kind === 'message' ? { ...item, carriesActions: index === lastTextIndex } : item
  ));
}

type RunCacheEntry = { items: ConversationItem[]; run: ChatUIMessage[] };

// Les objets message sont stables entre deux rendus quand rien ne change : on
// mémorise la projection par message de tête pour que `MessageBubble` (memo)
// ne se re-rende pas à chaque frame du stream pour l'historique.
const runCache = new WeakMap<ChatUIMessage, RunCacheEntry>();

function hasAnsweredAskUser(run: ChatUIMessage[]) {
  return run.some((message) => message.parts.some((part) => (
    isAskUserToolPart(part) && isAskUserPartAnswered(part)
  )));
}

function mergeRun(run: ChatUIMessage[]): ChatUIMessage {
  if (run.length === 1) {
    return run[0]!;
  }

  return { ...run[0]!, parts: run.flatMap((message) => message.parts) };
}

function projectRun(run: ChatUIMessage[]): ConversationItem[] {
  if (!hasAnsweredAskUser(run)) {
    return [{ carriesActions: true, key: run[0]!.id, kind: 'message', message: mergeRun(run) }];
  }

  return projectAssistantRun(run);
}

function projectRunCached(run: ChatUIMessage[]): ConversationItem[] {
  const head = run[0]!;
  const cached = runCache.get(head);

  if (cached && cached.run.length === run.length && cached.run.every((message, index) => message === run[index])) {
    return cached.items;
  }

  const items = projectRun(run);
  runCache.set(head, { items, run });
  return items;
}

function hasPendingAskUser(message: ChatUIMessage) {
  return message.role === 'assistant' && message.parts.some((part) => (
    isAskUserToolPart(part) && !isAskUserPartAnswered(part)
  ));
}

/**
 * Tant que le dernier segment assistant porte une question `ask_user` sans
 * réponse, la carte de question est l'interaction attendue : copier/régénérer
 * sous elle n'a pas de sens (l'actionnable est la carte). Les actions du tour
 * reviennent dès que la question est répondue ou que le tour avance.
 */
function hideActionsWhileQuestionPending(items: ConversationItem[]): ConversationItem[] {
  const last = items.at(-1);
  if (!last || last.kind !== 'message' || !hasPendingAskUser(last.message)) {
    return items;
  }

  const result = [...items];
  for (let index = result.length - 1; index >= 0; index -= 1) {
    const item = result[index]!;
    if (item.kind === 'message' && item.message.role !== 'assistant') {
      break;
    }
    if (item.kind === 'message' && item.carriesActions) {
      result[index] = { ...item, carriesActions: false };
    }
  }
  return result;
}

/**
 * Liste d'éléments de conversation affichés : messages utilisateur, segments
 * assistant et bulles de réponse `ask_user`. Les messages assistant consécutifs
 * sont fusionnés (comportement historique), sans question répondue le résultat
 * est strictement identique à l'ancien rendu.
 */
export function buildConversationItems(messages: ChatUIMessage[]): ConversationItem[] {
  const items: ConversationItem[] = [];
  let index = 0;

  while (index < messages.length) {
    const message = messages[index]!;

    if (message.role !== 'assistant') {
      items.push({ carriesActions: true, key: message.id, kind: 'message', message });
      index += 1;
      continue;
    }

    const run: ChatUIMessage[] = [];
    while (index < messages.length && messages[index]!.role === 'assistant') {
      run.push(messages[index]!);
      index += 1;
    }

    items.push(...projectRunCached(run));
  }

  return hideActionsWhileQuestionPending(items);
}
